import {
  DocumentIcon,
  FilmIcon,
  PhotographIcon,
  UploadIcon,
} from '@heroicons/react/outline';
import { XIcon } from '@heroicons/react/solid';
import Image from 'next/image';
import React, { useCallback, useEffect, useState } from 'react';
import { type FileRejection, useDropzone } from 'react-dropzone';
import { toast } from 'sonner';

import clsxm from '@/lib/clsxm';
import uuidv4 from '@/lib/uuid';

import Spinner from '@/components/Spinner';

import { compressImage } from '@/utils/compressImage';
import { createClient } from '@/utils/supabase/component';

interface UploadedFile {
  name: string;
  path: string;
  size?: number;
  type?: string;
  url?: string;
  /** Duration in seconds for video files. Probed client-side before upload
   * via HTMLVideoElement metadata so the server-side script director can
   * plan scene lengths within the clip's actual length. Undefined for
   * non-video files or when metadata read fails. */
  durationSeconds?: number;
}

interface MultiFileUploadProps {
  onFilesChange: (files: Record<string, UploadedFile>) => void;
  accept?: string;
  acceptDescription?: string;
  maxFiles?: number;
  maxSizeBytes?: number;
  uploadPath?: string;
  disabled?: boolean;
  initialFiles?: Record<string, UploadedFile>;
  className?: string;
  label?: string;
  /** Fired whenever an upload starts or finishes so the parent can, e.g.,
   * block a submit button while files are still uploading. */
  onUploadingChange?: (uploading: boolean) => void;
  /** Upload PNGs as they are (unless over the size cap) so a transparent logo
   * or cut-out keeps its transparency instead of being flattened onto white. */
  preserveTransparency?: boolean;
}

const MultiFileUpload: React.FC<MultiFileUploadProps> = ({
  onFilesChange,
  accept = 'image/*,application/pdf,.doc,.docx,.txt',
  acceptDescription = 'Images, PDFs, and Documents',
  maxFiles = 10,
  maxSizeBytes = 10 * 1024 * 1024, // 10MB default
  uploadPath = 'uploads',
  disabled = false,
  initialFiles = {},
  className = '',
  label = 'Upload Files',
  onUploadingChange,
  preserveTransparency = false,
}) => {
  const supabaseClient = createClient();
  const [uploadedFiles, setUploadedFiles] =
    useState<Record<string, UploadedFile>>(initialFiles);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState<Set<string>>(new Set());

  // Surface upload activity to the parent (e.g. to block a submit button).
  const uploading = isUploading || uploadingFiles.size > 0;
  useEffect(() => {
    onUploadingChange?.(uploading);
    return () => onUploadingChange?.(false);
  }, [uploading, onUploadingChange]);

  // Probe video duration locally via HTMLVideoElement metadata. Resolves
  // null on error / non-video / timeout so the upload never blocks on it.
  const probeVideoDuration = (file: File): Promise<number | null> =>
    new Promise((resolve) => {
      if (!file.type.startsWith('video/')) return resolve(null);
      const objectUrl = URL.createObjectURL(file);
      const video = document.createElement('video');
      let settled = false;
      const finish = (dur: number | null) => {
        if (settled) return;
        settled = true;
        URL.revokeObjectURL(objectUrl);
        video.removeAttribute('src');
        resolve(dur);
      };
      video.preload = 'metadata';
      video.muted = true;
      video.onloadedmetadata = () => {
        finish(Number.isFinite(video.duration) ? video.duration : null);
      };
      video.onerror = () => finish(null);
      // Safari sometimes fails silently; bail after 5s so the dropzone
      // never spins forever on a corrupt clip.
      setTimeout(() => finish(null), 5000);
      video.src = objectUrl;
    });

  const handleFileUpload = async (rawFile: File) => {
    // Downscale/re-encode oversized images so large phone/camera photos
    // upload quickly instead of being rejected for exceeding the size cap.
    const keepAsIs =
      preserveTransparency &&
      rawFile.type === 'image/png' &&
      rawFile.size <= maxSizeBytes;
    const file =
      rawFile.type.startsWith('image/') && !keepAsIs
        ? await compressImage(rawFile, { force: true, format: 'jpeg' })
        : rawFile;

    // Validate file size
    if (file.size > maxSizeBytes) {
      toast.error(
        `File "${file.name}" exceeds maximum size of ${maxSizeBytes / (1024 * 1024)}MB`,
      );
      return;
    }

    const fileId = uuidv4();
    const fileExt = file.name.split('.').pop();
    const fileName = `${fileId}.${fileExt}`;
    const filePath = `${uploadPath}/${fileName}`;

    try {
      // Add to uploading set
      setUploadingFiles((prev) => new Set(prev).add(fileId));

      // Read video duration in parallel with the upload so we don't block.
      const durationPromise = probeVideoDuration(file);

      const { data, error } = await supabaseClient.storage
        .from('uploads')
        .upload(filePath, file);

      if (error) {
        console.error('Upload error:', error);
        toast.error(`Failed to upload "${file.name}"`);
        setUploadingFiles((prev) => {
          const newSet = new Set(prev);
          newSet.delete(fileId);
          return newSet;
        });
        return;
      }

      // Get public URL
      const { data: publicUrlData } = supabaseClient.storage
        .from('uploads')
        .getPublicUrl(filePath);

      const durationSeconds = await durationPromise;
      const uploadedFile: UploadedFile = {
        name: file.name,
        path: filePath,
        size: file.size,
        type: file.type,
        url: publicUrlData.publicUrl,
        ...(durationSeconds != null ? { durationSeconds } : {}),
      };

      setUploadedFiles((prev) => {
        const updated = { ...prev, [fileId]: uploadedFile };
        onFilesChange(updated);
        return updated;
      });

      // Remove from uploading set
      setUploadingFiles((prev) => {
        const newSet = new Set(prev);
        newSet.delete(fileId);
        return newSet;
      });
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(`Failed to upload "${file.name}"`);
      setUploadingFiles((prev) => {
        const newSet = new Set(prev);
        newSet.delete(fileId);
        return newSet;
      });
    }
  };

  const onDrop = useCallback(
    async (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      if (disabled) return;

      // The dropzone itself has no file limit (see maxFiles below): when a
      // pick exceeded it, react-dropzone rejected EVERY file and nothing was
      // uploaded or reported. Rejections now only come from the file type.
      if (fileRejections.length > 0) {
        toast.error(
          fileRejections.length === 1
            ? `"${fileRejections[0].file.name}" isn't a supported file type (${acceptDescription})`
            : `${fileRejections.length} files aren't a supported file type (${acceptDescription})`,
        );
      }
      if (acceptedFiles.length === 0) return;

      const freeSlots =
        maxFiles - Object.keys(uploadedFiles).length - uploadingFiles.size;
      if (freeSlots <= 0) {
        toast.error(`Maximum ${maxFiles} files allowed`);
        return;
      }
      const filesToUpload = acceptedFiles.slice(0, freeSlots);
      if (acceptedFiles.length > freeSlots) {
        toast.info(
          freeSlots === 1
            ? `Up to ${maxFiles} files allowed, so only the first file was added`
            : `Up to ${maxFiles} files allowed, so only the first ${freeSlots} were added`,
        );
      }

      setIsUploading(true);

      // Upload files in parallel with error handling for each
      await Promise.allSettled(
        filesToUpload.map((file) => handleFileUpload(file)),
      );

      setIsUploading(false);
    },
    [disabled, uploadedFiles, uploadingFiles, maxFiles, acceptDescription],
  );

  const removeFile = async (fileId: string) => {
    const file = uploadedFiles[fileId];
    if (!file) return;

    try {
      // Delete from Supabase storage
      const { error } = await supabaseClient.storage
        .from('uploads')
        .remove([file.path]);

      if (error) {
        console.error('Delete error:', error);
        toast.error('Failed to remove file');
        return;
      }

      setUploadedFiles((prev) => {
        const updated = { ...prev };
        delete updated[fileId];
        onFilesChange(updated);
        return updated;
      });
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to remove file');
    }
  };

  // Parse accept prop to create dropzone accept object
  const getAcceptObject = () => {
    const acceptObj: Record<string, string[]> = {};

    if (accept.includes('image/*')) {
      acceptObj['image/*'] = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'];
    }
    if (accept.includes('video/*')) {
      acceptObj['video/*'] = [
        '.mp4',
        '.mov',
        '.webm',
        '.mkv',
        '.avi',
        '.m4v',
        '.3gp',
      ];
    }
    if (accept.includes('application/pdf') || accept.includes('.pdf')) {
      acceptObj['application/pdf'] = ['.pdf'];
    }
    if (accept.includes('.doc') || accept.includes('.docx')) {
      acceptObj['application/msword'] = ['.doc'];
      acceptObj[
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ] = ['.docx'];
    }
    if (accept.includes('.txt')) {
      acceptObj['text/plain'] = ['.txt'];
    }
    if (accept.includes('.csv')) {
      acceptObj['text/csv'] = ['.csv'];
    }
    if (accept.includes('.xlsx') || accept.includes('.xls')) {
      acceptObj['application/vnd.ms-excel'] = ['.xls'];
      acceptObj[
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ] = ['.xlsx'];
    }

    // If no specific types were matched, allow all
    if (Object.keys(acceptObj).length === 0) {
      return undefined;
    }

    return acceptObj;
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: getAcceptObject(),
    // 0 = no limit; onDrop applies maxFiles itself (extra files are dropped
    // with a message instead of the whole pick being rejected).
    maxFiles: 0,
    multiple: true,
    disabled: disabled || isUploading,
  });

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (file: UploadedFile) => {
    if (file.type?.startsWith('image/')) {
      return <PhotographIcon className='h-5 w-5' />;
    }
    if (file.type?.startsWith('video/')) {
      return <FilmIcon className='h-5 w-5' />;
    }
    return <DocumentIcon className='h-5 w-5' />;
  };

  const isPreviewable = (file: UploadedFile) =>
    file.type?.startsWith('image/') || file.type?.startsWith('video/');

  const filesArray = Object.entries(uploadedFiles);
  const hasFiles = filesArray.length > 0 || uploadingFiles.size > 0;

  return (
    <div className={clsxm('w-full', className)}>
      {/* {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-slate-400 mb-2">
          {label}
        </label>
      )} */}

      {/* File List */}
      {hasFiles && (
        <div className='mb-4'>
          {/* Thumbnail grid for image + video files */}
          {filesArray.some(([, file]) => isPreviewable(file)) && (
            <div className='grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-4'>
              {filesArray
                .filter(([, file]) => isPreviewable(file))
                .map(([fileId, file]) => (
                  <div
                    key={fileId}
                    className='relative group rounded-lg overflow-hidden border border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-700/50'
                  >
                    <div className='aspect-square relative bg-black/5 dark:bg-black/20'>
                      {file.type?.startsWith('video/') ? (
                        <>
                          <video
                            src={file.url}
                            className='w-full h-full object-cover'
                            muted
                            playsInline
                            preload='metadata'
                          />
                          <div className='absolute bottom-1 left-1 px-1.5 py-0.5 rounded bg-black/60 text-white text-[10px] font-medium tracking-wide flex items-center gap-1'>
                            <FilmIcon className='h-3 w-3' />
                            VIDEO
                          </div>
                        </>
                      ) : (
                        file.url && (
                          <Image
                            src={file.url}
                            alt={file.name}
                            fill
                            sizes='(max-width: 640px) 50vw, (max-width: 768px) 33vw, 200px'
                            className='object-cover'
                          />
                        )
                      )}
                      {/* Overlay on hover */}
                      <div className='absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200 flex items-center justify-center'>
                        <button
                          type='button'
                          onClick={() => removeFile(fileId)}
                          disabled={disabled}
                          className='opacity-0 group-hover:opacity-100 transition-opacity duration-200 p-2 bg-red-500 hover:bg-red-600 rounded-full text-white'
                          aria-label={`Remove ${file.name}`}
                        >
                          <XIcon className='h-4 w-4' />
                        </button>
                      </div>
                    </div>
                    {/* File name below thumbnail */}
                    <div className='p-2'>
                      <p
                        className='text-xs text-gray-600 dark:text-gray-400 truncate'
                        title={file.name}
                      >
                        {file.name}
                      </p>
                      {file.size && (
                        <p className='text-xs text-gray-500 dark:text-gray-500'>
                          {formatFileSize(file.size)}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
            </div>
          )}

          {/* List view for non-previewable files (PDFs, docs, etc.) */}
          <div className='space-y-2'>
            {filesArray
              .filter(([, file]) => !isPreviewable(file))
              .map(([fileId, file]) => (
                <div
                  key={fileId}
                  className='flex items-center justify-between p-3 bg-gray-50 dark:bg-slate-700/50 rounded-lg border border-gray-200 dark:border-slate-600'
                >
                  <div className='flex items-center space-x-3'>
                    <div className='text-gray-500 dark:text-gray-400'>
                      {getFileIcon(file)}
                    </div>
                    <div>
                      <p className='text-sm font-medium text-gray-900 dark:text-white truncate max-w-xs'>
                        {file.name}
                      </p>
                      {file.size && (
                        <p className='text-xs text-gray-500 dark:text-gray-400'>
                          {formatFileSize(file.size)}
                        </p>
                      )}
                    </div>
                  </div>
                  <button
                    type='button'
                    onClick={() => removeFile(fileId)}
                    disabled={disabled}
                    className='p-1 text-gray-400 hover:text-red-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                    aria-label={`Remove ${file.name}`}
                  >
                    <XIcon className='h-5 w-5' />
                  </button>
                </div>
              ))}
          </div>

          {/* Show uploading indicator for files being uploaded */}
          {uploadingFiles.size > 0 && (
            <div className='p-3 bg-gray-50 dark:bg-slate-700/50 rounded-lg border border-gray-200 dark:border-slate-600'>
              <div className='flex items-center space-x-2'>
                <Spinner className='h-4 w-4 animate-spin text-primary-500' />
                <span className='text-sm text-gray-600 dark:text-gray-300'>
                  Uploading {uploadingFiles.size} file
                  {uploadingFiles.size > 1 ? 's' : ''}...
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Dropzone */}
      {Object.keys(uploadedFiles).length < maxFiles && (
        <div
          {...getRootProps()}
          className={clsxm(
            'flex flex-col items-center justify-center p-4 border-2 border-dashed rounded-lg cursor-pointer transition-all',
            isDragActive
              ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
              : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-slate-700/50',
            disabled && 'opacity-50 cursor-not-allowed',
            isUploading && 'pointer-events-none',
          )}
        >
          <input {...getInputProps()} />

          {isUploading ? (
            <div className='flex flex-col items-center'>
              <Spinner className='h-6 w-6 animate-spin text-primary-500 mb-1' />
              <p className='text-sm text-gray-600 dark:text-gray-400'>
                Uploading files...
              </p>
            </div>
          ) : (
            <>
              <UploadIcon className='h-6 w-6 text-gray-400 dark:text-gray-500 mb-1' />
              <p className='text-sm font-medium text-gray-700 dark:text-gray-300'>
                {isDragActive
                  ? 'Drop files here'
                  : 'Click or drag files to upload'}
              </p>
              <p className='text-xs text-gray-500 dark:text-gray-400 mt-1'>
                {acceptDescription} • {filesArray.length}/{maxFiles} files • Max{' '}
                {maxSizeBytes / (1024 * 1024)}MB
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default MultiFileUpload;
